import type { FocusRunState } from "@mind/contracts/browser";
import { useQueries } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { useTranslation } from "react-i18next";
import { isAbortError } from "../../api/client.js";
import { acceptOrderedEvents, boundedEventCursor, FocusDeltaBatcher, FOCUS_SOURCE_LIMIT } from "./events.js";
import { focusApi } from "./focusApi.js";
import { focusQueryKeys } from "./queryKeys.js";
import styles from "./FocusScreen.module.css";

const TERMINAL_STATES: Readonly<Record<FocusRunState, boolean>> = {
  queued: false,
  planning: false,
  awaiting_query_review: false,
  searching: false,
  synthesizing: false,
  cancelling: false,
  cancelled: true,
  failed: true,
  interrupted: true,
  succeeded: true,
};

const SAFE_MARKDOWN_COMPONENTS: Components = {
  a: ({ children, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer">{children}</a>,
  img: ({ alt }) => alt === undefined ? null : <span>{alt}</span>,
};
const MARKDOWN_PLUGINS = [remarkGfm];

export function isTerminalFocusState(state: FocusRunState): boolean {
  return TERMINAL_STATES[state];
}

type RunEventPanelProps = {
  readonly runId: string;
  readonly authoritativeSequence: string;
  readonly initialState: FocusRunState;
  readonly showDelta: boolean;
  readonly cancelPending: boolean;
  readonly retryPending: boolean;
  readonly retryAvailable: boolean;
  readonly onBoundary: (state: FocusRunState) => void;
  readonly onTerminal: () => void;
  readonly onContent: () => void;
  readonly onCancel: () => void;
  readonly onRetry: () => void;
};

export function RunEventPanel({
  runId,
  authoritativeSequence,
  initialState,
  showDelta,
  cancelPending,
  retryPending,
  retryAvailable,
  onBoundary,
  onTerminal,
  onContent,
  onCancel,
  onRetry,
}: RunEventPanelProps) {
  const { t } = useTranslation();
  const [streamText, setStreamText] = useState("");
  const [sourceIds, setSourceIds] = useState<readonly string[]>([]);
  const [observedState, setObservedState] = useState(initialState);
  const [pollError, setPollError] = useState(false);
  const boundaryRef = useRef(onBoundary);
  const terminalRef = useRef(onTerminal);
  const contentRef = useRef(onContent);
  const showDeltaRef = useRef(showDelta);
  const initialSequenceRef = useRef(authoritativeSequence);
  const initialStateRef = useRef(initialState);
  boundaryRef.current = onBoundary;
  terminalRef.current = onTerminal;
  contentRef.current = onContent;
  showDeltaRef.current = showDelta;
  useEffect(() => {
    const controller = new AbortController();
    const sourceSet = new Set<string>();
    const batcher = new FocusDeltaBatcher((text) => {
      setStreamText((current) => `${current}${text}`);
      contentRef.current();
    });
    let cursor = boundedEventCursor(initialSequenceRef.current);
    let timer: number | null = null;
    let pagesWithoutPause = 0;
    let polledState = initialStateRef.current;
    let terminalObserved = isTerminalFocusState(polledState);
    let stopped = false;
    const deadline = Date.now() + 15 * 60_000;

    const stop = (terminal: boolean): void => {
      if (stopped) return;
      stopped = true;
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
      batcher.dispose();
      if (terminal) terminalRef.current();
      else setPollError(true);
    };

    const poll = async (): Promise<void> => {
      if (stopped || Date.now() >= deadline) {
        stop(false);
        return;
      }
      try {
        const page = await focusApi.getEvents(runId, cursor, 200, controller.signal);
        if (stopped) return;
        setPollError(false);
        const selection = acceptOrderedEvents(runId, cursor, page.items);
        cursor = selection.lastSequence;
        for (const frame of selection.accepted) {
          if (frame.payload.kind === "delta") {
            if (showDeltaRef.current) batcher.push(frame.payload.text);
            continue;
          }
          if (frame.payload.kind === "source") {
            if (sourceSet.size < FOCUS_SOURCE_LIMIT && !sourceSet.has(frame.payload.sourceId)) {
              sourceSet.add(frame.payload.sourceId);
              setSourceIds(Array.from(sourceSet));
            }
            continue;
          }
          if (frame.payload.state !== polledState) {
            polledState = frame.payload.state;
            setObservedState(polledState);
            boundaryRef.current(polledState);
          }
          if (isTerminalFocusState(frame.payload.state)) terminalObserved = true;
        }

        if (terminalObserved && !page.hasMore) {
          batcher.flushNow();
          stop(true);
          return;
        }
        pagesWithoutPause = page.hasMore ? pagesWithoutPause + 1 : 0;
        const delay = page.hasMore && pagesWithoutPause < 5 ? 0 : page.hasMore ? 40 : 900;
        if (delay > 0) pagesWithoutPause = 0;
        timer = window.setTimeout(() => void poll(), delay);
      } catch (error) {
        if (isAbortError(error) || stopped) return;
        setPollError(true);
        timer = window.setTimeout(() => void poll(), 1_500);
      }
    };

    void poll();
    return () => {
      stopped = true;
      controller.abort();
      if (timer !== null) window.clearTimeout(timer);
      batcher.dispose();
    };
  }, [runId]);

  const terminal = isTerminalFocusState(observedState);
  const cancelling = observedState === "cancelling" || cancelPending;
  const retryable = observedState === "failed" || observedState === "interrupted" || observedState === "cancelled";
  const animate = !pollError && !terminal && !cancelling && observedState !== "awaiting_query_review";

  return (
    <section className={styles.runEvents} aria-label={t("focus.currentRun")} aria-busy={!terminal && !pollError}>
      {showDelta && streamText.length > 0 ? (
        <article className={styles.assistantMessage}>
          <p className={styles.messageLabel}>{t("focus.assistant")}</p>
          <div className={styles.documentBody}>
            <ReactMarkdown
              remarkPlugins={MARKDOWN_PLUGINS}
              skipHtml
              urlTransform={markdownUrlTransform}
              components={SAFE_MARKDOWN_COMPONENTS}
            >
              {streamText}
            </ReactMarkdown>
          </div>
        </article>
      ) : null}
      {pollError ? <p className={styles.inlineNotice}>{t("focus.pollDelayed")}</p> : null}
      <SourceCitations sourceIds={sourceIds} />
      {observedState !== "succeeded" ? (
        <div className={styles.runFeedback} data-animating={animate}>
          <span className={styles.runPhase}>
            {!terminal ? <span className={styles.waitingDot} aria-hidden="true" /> : null}
            {t(`focus.states.${cancelling ? "cancelling" : observedState}`)}
          </span>
          {!terminal && !cancelling ? (
            <button type="button" disabled={cancelPending} onClick={onCancel}>{t("focus.cancel")}</button>
          ) : retryable && retryAvailable ? (
            <button type="button" disabled={retryPending} onClick={onRetry}>{t("focus.retry")}</button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}


function markdownUrlTransform(url: string): string {
  const normalized = url.trim();
  if (normalized.length === 0) return "";
  try {
    const parsed = new URL(normalized, window.location.origin);
    return parsed.protocol === "http:" || parsed.protocol === "https:" || parsed.protocol === "mailto:" ? normalized : "";
  } catch {
    return "";
  }
}

function SourceCitations({ sourceIds }: { readonly sourceIds: readonly string[] }) {
  const { t } = useTranslation();
  const sourceQueries = useQueries({
    queries: sourceIds.map((sourceId) => ({
      queryKey: focusQueryKeys.source(sourceId),
      queryFn: ({ signal }: { signal: AbortSignal }) => focusApi.getSource(sourceId, signal),
      staleTime: 60_000,
    })),
  });
  if (sourceIds.length === 0) return null;

  return (
    <section className={styles.citations} aria-labelledby={`sources-${sourceIds[0]}`}>
      <h3 id={`sources-${sourceIds[0]}`}>{t("focus.sources")}</h3>
      <ol>
        {sourceQueries.map((sourceQuery, index) => {
          const source = sourceQuery.data;
          return (
            <li key={sourceIds[index]}>
              {source === undefined ? (
                <span>{t("focus.sourceLoading")}</span>
              ) : (
                <a href={source.url} target="_blank" rel="noreferrer">
                  {source.title.length > 0 ? source.title : source.url}
                </a>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
